import type { Attachment, Submittal, SubmittalDecideOutcome, SubmittalFileKind, SubmittalRound } from '@/lib/apiClient';
import { addDays, easternToday, localYmd } from '@/modules/manpower/lib/gridDates';
import { receivedRangeProblem } from './submittalEdit';

/**
 * Review rounds on the submittal detail: how a round reads, which files belong to it,
 * and what a file uploaded to it is called. Pure, so the screen's components stay
 * about layout.
 */

export const OUTCOME_LABEL: Record<SubmittalDecideOutcome, string> = {
  approved: 'Approved',
  'approved-as-noted': 'Approved as Noted',
  'revise-resubmit': 'Revise & Resubmit',
  rejected: 'Rejected',
};

/** "Rev 0", "Rev 1"… The number is stored as an integer; this is the only place it is worded. */
export const revisionLabel = (revision: number): string => `Rev ${revision}`;

export type RoundStage = 'received' | 'in-review' | 'returned';

/** Where a round stands, from its own dates (not from the submittal's status). */
export function roundStage(round: SubmittalRound): RoundStage {
  if (round.returnedAt) return 'returned';
  if (round.sentAt) return 'in-review';
  return 'received';
}

/** The short state shown on a round's tab: its outcome once returned, otherwise its stage. */
export function roundState(round: SubmittalRound): string {
  const stage = roundStage(round);
  if (stage === 'returned') return round.outcome ? OUTCOME_LABEL[round.outcome] : 'Returned';
  return stage === 'in-review' ? 'In review' : 'Received';
}

/** The round the submittal is in now: the highest revision. */
export function latestRound(rounds: readonly SubmittalRound[]): SubmittalRound | undefined {
  return rounds.reduce<SubmittalRound | undefined>((best, r) => (!best || r.revision > best.revision ? r : best), undefined);
}

/** The revision Send Revision would open: one past the latest, or Rev 1 if none is recorded yet. */
export function nextRevision(rounds: readonly SubmittalRound[]): number {
  const latest = latestRound(rounds);
  return latest ? latest.revision + 1 : 1;
}

/**
 * Whether the recorded send may be cleared ("Mark as not sent"): only a FIRST review
 * with no decision (#626). A revision's send has already cleared the previous decision
 * date, replaced the due date and opened a round, none of which can be put back, so
 * there the time can be corrected and not cleared. The API enforces the same rule (409);
 * this only decides whether the button is offered. `rounds` is checked when it has loaded.
 */
export function canMarkNotSent(sub: Submittal, rounds?: readonly SubmittalRound[]): boolean {
  if (sub.decision != null || sub.decidedAt != null) return false;
  if (sub.status !== 'queued' && sub.status !== 'in-review') return false;
  return rounds ? (latestRound(rounds)?.revision ?? 0) === 0 : true;
}

/** Days a reviewer is given for a revision, counted from the day it is sent. The API's `REVIEW_DAYS`. */
const REVISION_REVIEW_DAYS = 14;

/**
 * The review due date a revision sent at `now` will get: 14 days after today's date IN
 * EASTERN, as `YYYY-MM-DD`. Quoted in the revision draft, which is written before the
 * API sets the real one (#626); it has to be the same day, and after 8 PM Eastern the
 * browser's UTC date is already tomorrow.
 *
 * Built from the shared Eastern helpers: `easternToday` is the Eastern calendar day at
 * local midnight, so `addDays` is day arithmetic on a date, not on an instant, and a DST
 * change in between cannot move it.
 */
export function revisionDueYmd(now: Date = new Date()): string {
  return localYmd(addDays(easternToday(now), REVISION_REVIEW_DAYS));
}

export interface RoundFiles {
  submitted: Attachment[];
  returned: Attachment[];
}

/**
 * A submittal's files sorted onto its rounds.
 *
 * A file with no round, or on a round that is not in the list, goes to `unassigned`
 * rather than being dropped: the previous release uploaded files with no round, and
 * if the backfill missed one it must still be on the screen where someone can open it.
 */
export function filesByRound(
  files: readonly Attachment[],
  rounds: readonly SubmittalRound[],
): { byRound: Map<number, RoundFiles>; unassigned: Attachment[] } {
  const byRound = new Map<number, RoundFiles>(rounds.map((r) => [r.id, { submitted: [], returned: [] }]));
  const unassigned: Attachment[] = [];
  for (const f of files) {
    const bucket = f.roundId == null ? undefined : byRound.get(f.roundId);
    if (!bucket || !f.kind) unassigned.push(f);
    else bucket[f.kind].push(f);
  }
  return { byRound, unassigned };
}

/**
 * Why a received-from-sub day can't be sent, or null.
 *
 * The same bounds the API holds (it refuses anything else with a 400). The date input's
 * own min/max only paints a value invalid, and the dialog's button is not a form submit,
 * so a two-digit year a browser zero-pads to 0026 would otherwise go out.
 */
export function receivedProblem(day: string): string | null {
  if (day.trim() === '') return 'Enter the day the corrected package was received.';
  return receivedRangeProblem(day);
}

/**
 * The document number in a file's name. Rev 0 keeps the submittal's own number, so the
 * original is named as it always was; a later round appends its revision
 * (`SUB-002-R1`), because in the Job Folder the name is all there is to tell the
 * rounds apart.
 */
export function attachmentDocNumber(submittalNumber: string | null, revision: number): string | null {
  const base = submittalNumber?.trim() || null;
  if (revision === 0) return base;
  return base ? `${base}-R${revision}` : `R${revision}`;
}

/** The status in a file's name: a returned markup is RETURNED, whatever the submittal's status is. */
export function attachmentStatus(kind: SubmittalFileKind, submittalStatus: string): string {
  return kind === 'returned' ? 'returned' : submittalStatus;
}
